import dayjs, { Dayjs, isDayjs } from 'dayjs';

/** Non-null, non-empty after trim — suitable for date string inputs. */
export function isValidDate(value: string | null | undefined): value is string {
  return value != null && String(value).trim() !== '';
}

/** Comparable local calendar day (Y-M-D), ignoring time — for ordering / range checks. */
export function localDayKey(d: Dayjs): number {
  return Date.UTC(d.year(), d.month(), d.date());
}

/** Whether `day` falls on a local calendar day within [start, end], inclusive. */
export function isInclusiveDayRange(
  day: Dayjs,
  start: Dayjs,
  end: Dayjs,
): boolean {
  const t = localDayKey(day);
  const s = localDayKey(start);
  const e = localDayKey(end);
  return t >= s && t <= e;
}

/**
 * True only when both strings are non-empty, parse as dates, and `asOf` (local calendar)
 * lies within [start, end] inclusive. Otherwise false (including missing/blank input).
 */
export function isBetween(
  startDate: string | null | undefined,
  endDate: string | null | undefined,
  asOf: Dayjs = dayjs(),
): boolean {
  if (!isValidDate(startDate) || !isValidDate(endDate)) {
    return false;
  }

  const start = dayjs(startDate);
  const end = dayjs(endDate);

  if (!start.isValid() || !end.isValid()) {
    return false;
  }

  return isInclusiveDayRange(asOf, start, end);
}

/**
 * True only when `date` is non-empty, parses, and `asOf`'s local calendar day is strictly
 * before `date`'s. false otherwise.
 */
export function isBefore(
  date: string | null | undefined,
  asOf: Dayjs = dayjs(),
): boolean {
  if (!isValidDate(date)) {
    return false;
  }

  const d = dayjs(date);
  if (!d.isValid()) {
    return false;
  }

  return localDayKey(asOf) < localDayKey(d);
}

/**
 * True only when `date` is non-empty, parses, and `asOf`'s local calendar day is strictly
 * after `date`'s. false otherwise.
 */
export function isAfter(
  date: string | null | undefined,
  asOf: Dayjs = dayjs(),
): boolean {
  if (!isValidDate(date)) {
    return false;
  }

  const d = dayjs(date);
  if (!d.isValid()) {
    return false;
  }

  return localDayKey(asOf) > localDayKey(d);
}

export function safeParse(value: number | null | undefined) {
  try {
    if (value && !Number.isNaN(+value)) {
      return dayjs.unix(+value);
    }
  } catch (e) {
    return null;
  }

  return null;
}

export function toUTCTimestamp(date: Dayjs | null) {
  if (date && isDayjs(date)) {
    if (!date.isValid()) {
      return null;
    }

    return date.startOf('day').utc(true).unix();
  }

  return null;
}
